import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useDisconnectGoogleAccount,
  useGoogleConnectionStatus,
} from "@/hooks/use-google-connection";
import { authClient, useSession } from "@/lib/auth-client";

/**
 * Admin-only "Connect Google Account" panel (EPIC-013) — the shared Google
 * identity every Google Calendar invite is organized by and every Google
 * Contacts search runs against. Gated on role, same as this app's other
 * admin-only surfaces (Audit Log, admin email routes); the row-action send
 * itself is available to any signed-in user (see the EPIC-013 build spec).
 *
 * Connecting/reconnecting goes through Better Auth's built-in account-linking
 * (authClient.linkSocial) — there's no custom connect route. Status and
 * disconnect are custom admin routes because the connection is treated as a
 * SINGLETON shared identity, not a per-user linked account (see
 * .claude/rules/google-calendar.md).
 */
export function GoogleCalendarConnectionCard() {
  const { data: session } = useSession();
  const isAdmin = session?.user.role === "admin";
  const status = useGoogleConnectionStatus();
  const disconnect = useDisconnectGoogleAccount();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!isAdmin) return null;

  function handleConnect() {
    // A path-only callbackURL (even the real nested /dashboard/settings
    // route) redirects to a blank page after Google's consent screen on
    // sandbox — confirmed by manual testing. The bare origin root works
    // reliably, so redirect there instead. Built from window.location.origin
    // (not a hardcoded host) so this works unchanged on local dev, sandbox,
    // and any future environment.
    void authClient.linkSocial({
      provider: "google",
      callbackURL: `${window.location.origin}/`,
    });
  }

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle>Google Calendar</CardTitle>
        <CardDescription>
          The shared Google account used to send calendar invites and search Google Contacts for
          recipient autocomplete.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Badge variant={status.data?.connected ? "success" : "secondary"} className="self-start">
          {status.isLoading
            ? "Checking…"
            : status.data?.connected
              ? `Connected as ${status.data.email ?? "unknown"}`
              : "Not connected"}
        </Badge>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={handleConnect}>
            {status.data?.connected ? "Reconnect" : "Connect Google Account"}
          </Button>
          {status.data?.connected && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirmOpen(true)}
              disabled={disconnect.isPending}
            >
              Disconnect
            </Button>
          )}
        </div>
      </CardContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Disconnect Google account"
        description="This breaks calendar invite sending and contacts search for everyone until it's reconnected. Continue?"
        confirmLabel="Disconnect"
        variant="destructive"
        loading={disconnect.isPending}
        onConfirm={() => disconnect.mutate(undefined, { onSuccess: () => setConfirmOpen(false) })}
      />
    </Card>
  );
}
